/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { ApkDetail } from "./apk-detail-types"

/**
 * Builds external links for the APK.
 *
 * Store links use the package name; Koodous and VirusTotal use the APK hash.
 * The links are not checked for a matching result.
 *
 * @param apk - APK identifiers used to build the links.
 * @returns External links for the APK.
 */
export function externalLinks({ packageName, apkSignature }: Pick<ApkDetail, "packageName" | "apkSignature">) {
    return [
        { label: "Google Play", href: withQuery("https://play.google.com/store/apps/details", { id: packageName }) },
        {
            label: "Huawei AppGallery",
            href: withQuery("https://appgallery.cloud.huawei.com/appDetail", { pkgName: packageName })
        },
        { label: "Koodous by APK hash", href: `https://koodous.com/apks/${encodeURIComponent(apkSignature)}` },
        {
            label: "Koodous by package name",
            href: withQuery("https://koodous.com/apks", { search: `package_name:${packageName}` })
        },
        { label: "VirusTotal", href: `https://www.virustotal.com/gui/file/${encodeURIComponent(apkSignature)}` }
    ]
}

/**
 * Adds encoded query parameters to a URL.
 *
 * @param base - URL without query parameters.
 * @param params - Query parameter names and values.
 * @returns The URL with the encoded query string.
 */
function withQuery(base: string, params: Record<string, string>): string {
    return `${base}?${new URLSearchParams(params)}`
}
